import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, Redirect } from "wouter";
import { lazy, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import NetworkStatusProvider from "./components/NetworkStatusProvider";
import { ThemeProvider } from "./contexts/ThemeContext";

// 全域載入畫面
function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        <p className="text-sm text-muted-foreground">載入中...</p>
      </div>
    </div>
  );
}

// ===== 所有頁面使用 React.lazy 動態載入 =====
const NotFound = lazy(() => import("@/pages/NotFound"));
const Home = lazy(() => import("./pages/Home"));
const Organization = lazy(() => import("./pages/Organization"));
const Personnel = lazy(() => import("./pages/Personnel"));
const Sites = lazy(() => import("./pages/Sites"));
const Documents = lazy(() => import("./pages/Documents"));
const Training = lazy(() => import("./pages/Training"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Announcements = lazy(() => import("./pages/Announcements"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Salary = lazy(() => import("./pages/Salary"));
const Users = lazy(() => import("./pages/Users"));
const PromotionHistory = lazy(() => import("./pages/PromotionHistory"));
const RankLeaderboard = lazy(() => import("./pages/RankLeaderboard"));
const DailyCheckin = lazy(() => import("./pages/DailyCheckin"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Shop = lazy(() => import("./pages/Shop"));
const TeamChallenges = lazy(() => import("./pages/TeamChallenges"));
const VipCenter = lazy(() => import("./pages/VipCenter"));
const SiteDetail = lazy(() => import("./pages/SiteDetail"));
const SiteDashboard = lazy(() => import("./pages/SiteDashboard"));
const ProfitAnalysis = lazy(() => import("./pages/ProfitAnalysis"));
const MarqueeManagement = lazy(() => import("./pages/MarqueeManagement"));
const ShopManagement = lazy(() => import("./pages/ShopManagement"));
const Discussions = lazy(() => import("./pages/Discussions"));
const PointsLeaderboard = lazy(() => import("./pages/PointsLeaderboard"));
const EagleRanks = lazy(() => import("./pages/EagleRanks"));
const EagleGuide = lazy(() => import("./pages/EagleGuide"));
const CurrencyGuide = lazy(() => import("./pages/CurrencyGuide"));
const EagleAchievements = lazy(() => import("./pages/EagleAchievements"));
const BackupManagement = lazy(() => import("./pages/BackupManagement"));
const PersonnelNew = lazy(() => import("./pages/PersonnelNew"));
const BulkImport = lazy(() => import("./pages/BulkImport"));
const ChangeRequests = lazy(() => import("./pages/ChangeRequests"));
const ProfileSetup = lazy(() => import("./pages/ProfileSetup"));
const ScheduledTasks = lazy(() => import("./pages/ScheduledTasks"));
const CertificateRenewal = lazy(() => import("./pages/CertificateRenewal"));
const PositionReports = lazy(() => import("./pages/PositionReports"));
const PositionBudget = lazy(() => import("./pages/PositionBudget"));
const DropdownOptionsManager = lazy(() => import("./pages/DropdownOptionsManager"));
const ContractReminders = lazy(() => import("./pages/ContractReminders"));
const ContractVersions = lazy(() => import("./pages/ContractVersions"));
const SiteBulkImport = lazy(() => import("./pages/SiteBulkImport"));
const SystemNotifications = lazy(() => import("./pages/SystemNotifications"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const ShopProducts = lazy(() => import("./pages/ShopProducts"));
const ShopCategories = lazy(() => import("./pages/ShopCategories"));
const ShopOrders = lazy(() => import("./pages/ShopOrders"));
const Branches = lazy(() => import("./pages/Branches"));
const CompanyManagement = lazy(() => import("./pages/CompanyManagement"));
const ModuleOptionsManager = lazy(() => import("./pages/ModuleOptionsManager"));
const ShopStore = lazy(() => import("./pages/ShopStore"));
const ShopMyOrders = lazy(() => import("./pages/ShopMyOrders"));
const AdminLevelPermissions = lazy(() => import("./pages/AdminLevelPermissions"));
const DocumentCategories = lazy(() => import("./pages/DocumentCategories"));
const TrainingCheckin = lazy(() => import("./pages/TrainingCheckin"));
const TrainingCertPDF = lazy(() => import("./pages/TrainingCertPDF"));
const GameZone = lazy(() => import("./pages/GameZone"));
const FlashSaleAdmin = lazy(() => import("./pages/FlashSaleAdmin"));
const AdminRewardPanel = lazy(() => import("./pages/AdminRewardPanel"));
const LuckyMomentSettings = lazy(() => import("./pages/LuckyMomentSettings"));
const LoginAuditLogs = lazy(() => import("./pages/LoginAuditLogs"));
const SystemSettings = lazy(() => import("./pages/SystemSettings"));
const CurrencyGrant = lazy(() => import("./pages/CurrencyGrant"));
const SiteCostRecords = lazy(() => import("./pages/SiteCostRecords"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const LoginPosterManagement = lazy(() => import("./pages/LoginPosterManagement"));
const FBPostModule = lazy(() => import("./pages/FBPostModule"));
const DashboardLayout = lazy(() => import("./components/DashboardLayout"));

// named exports 需要特殊處理
const Login = lazy(() => import("./pages/Login").then(m => ({ default: m.Login })));
const FirstLoginProfile = lazy(() => import("./pages/FirstLoginProfile").then(m => ({ default: m.FirstLoginProfile })));

function AdminRewardsPage() {
  return (
    <Suspense fallback={<PageLoading />}>
      <DashboardLayout>
        <AdminRewardPanel />
      </DashboardLayout>
    </Suspense>
  );
}

function LuckySettingsPage() {
  return (
    <Suspense fallback={<PageLoading />}>
      <DashboardLayout>
        <LuckyMomentSettings />
      </DashboardLayout>
    </Suspense>
  );
}

function CurrencyGrantPage() {
  return (
    <Suspense fallback={<PageLoading />}>
      <DashboardLayout>
        <CurrencyGrant />
      </DashboardLayout>
    </Suspense>
  );
}

function LoginPosterPage() {
  return (
    <Suspense fallback={<PageLoading />}>
      <DashboardLayout>
        <LoginPosterManagement />
      </DashboardLayout>
    </Suspense>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Switch>
        <Route path={"/login"} component={Login} />
        <Route path={"/first-login"} component={FirstLoginProfile} />
        <Route path={"/profile-setup"} component={ProfileSetup} />
        <Route path={"/"} component={Home} />
        <Route path={"/organization"} component={Organization} />
        <Route path={"/personnel"} component={Personnel} />
        <Route path={"/personnel/new"} component={PersonnelNew} />
        <Route path={"/sites"} component={Sites} />
        <Route path={"/sites/:id"} component={SiteDetail} />
        <Route path={"/site-dashboard"} component={SiteDashboard} />
        <Route path={"/profit-analysis"} component={ProfitAnalysis} />
        <Route path={"/documents"} component={Documents} />
        <Route path={"/training"} component={Training} />
        <Route path={"/training/checkin"} component={TrainingCheckin} />
        <Route path={"/training/cert/:id"} component={TrainingCertPDF} />
        <Route path={"/tasks"} component={Tasks} />
        <Route path={"/announcements"} component={Announcements} />
        <Route path={"/marquee-management"} component={MarqueeManagement} />
        <Route path={"/calendar"} component={Calendar} />
        <Route path={"/salary"} component={Salary} />
        <Route path={"/users"} component={Users} />
        <Route path={"/promotion-history"} component={PromotionHistory} />
        <Route path={"/rank-leaderboard"} component={RankLeaderboard} />
        <Route path={"/daily-checkin"} component={DailyCheckin} />
        <Route path={"/achievements"} component={Achievements} />
        <Route path={"/shop"} component={() => <Redirect to="/shop-store" />} />
        <Route path={"/shop-management"} component={ShopManagement} />
        <Route path={"/team-challenges"} component={TeamChallenges} />
        <Route path={"/vip"} component={VipCenter} />
        <Route path={"/discussions"} component={Discussions} />
        <Route path={"/points-leaderboard"} component={PointsLeaderboard} />
        <Route path={"/eagle-ranks"} component={EagleRanks} />
        <Route path={"/eagle-guide"} component={EagleGuide} />
        <Route path={"/currency-guide"} component={CurrencyGuide} />
        <Route path={"/eagle-achievements"} component={EagleAchievements} />
        <Route path={"/backup"} component={BackupManagement} />
        <Route path={"/bulk-import"} component={BulkImport} />
        <Route path={"/change-requests"} component={ChangeRequests} />
        <Route path={"/scheduled-tasks"} component={ScheduledTasks} />
        <Route path={"/certificate-renewal"} component={CertificateRenewal} />
        <Route path={"/position-reports"} component={PositionReports} />
        <Route path={"/position-budget"} component={PositionBudget} />
        <Route path={"/dropdown-options"} component={DropdownOptionsManager} />
        <Route path={"/contract-reminders"} component={ContractReminders} />
        <Route path={"/contract-versions/:siteId"} component={ContractVersions} />
        <Route path={"/site-bulk-import"} component={SiteBulkImport} />
        <Route path={"/system-notifications"} component={SystemNotifications} />
        <Route path={"/notification-settings"} component={NotificationSettings} />
        <Route path={"/shop-products"} component={ShopProducts} />
        <Route path={"/shop-categories"} component={ShopCategories} />
        <Route path={"/shop-orders"} component={ShopOrders} />
        <Route path={"/branches"} component={Branches} />
        <Route path={"/company-management"} component={CompanyManagement} />
        <Route path={"/module-options"} component={ModuleOptionsManager} />
        <Route path={"/shop-store"} component={ShopStore} />
        <Route path={"/shop-my-orders"} component={ShopMyOrders} />
        <Route path={"/admin-level-permissions"} component={AdminLevelPermissions} />
        <Route path={"/document-categories"} component={DocumentCategories} />
        <Route path={"/game-zone"} component={GameZone} />
        <Route path={"/flash-sale-admin"} component={FlashSaleAdmin} />
        <Route path={"/admin-rewards"} component={AdminRewardsPage} />
        <Route path={"/currency-grant"} component={CurrencyGrantPage} />
        <Route path={"/lucky-settings"} component={LuckySettingsPage} />
        <Route path={"/login-audit-logs"} component={LoginAuditLogs} />
        <Route path={"/settings"} component={SystemSettings} />
        <Route path={"/site-cost-records"} component={SiteCostRecords} />
        <Route path={"/knowledge-base"} component={KnowledgeBase} />
        <Route path={"/login-poster"} component={LoginPosterPage} />
        <Route path={"/fb-post"} component={FBPostModule} />
        <Route path={"/404"} component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <NetworkStatusProvider>
        <ThemeProvider defaultTheme="light" switchable={true}>
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </ThemeProvider>
      </NetworkStatusProvider>
    </ErrorBoundary>
  );
}

export default App;
